import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Portal from 'layout/atoms/Portal/Portal';
import styles from './Tooltip.module.scss';
// How close to the viewport edge is still considered "onscreen".
const VIEWPORT_MARGIN = 8;
// Keep the arrow at least this far from the tooltip's rounded corners.
const ARROW_EDGE_PADDING = 12;
function Tooltip({ theme = 'default', size = 'm', maxWidth = 400, position = 'top', delay = 2000, content, children, }) {
    const [visible, setVisible] = useState(false);
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const timeoutRef = useRef(null);
    const hideTimeoutRef = useRef(null);
    const [tooltipStyles, setTooltipStyles] = useState(undefined);
    // How far the arrow needs to shift from dead-center to keep pointing at the
    // trigger, once the tooltip box itself has been nudged back onscreen.
    const [arrowOffset, setArrowOffset] = useState(0);
    // The unclamped, trigger-centered position — clampToViewport reads from this
    // rather than the (possibly already-shifted) rendered styles, so repeated
    // clamps never compound on top of each other.
    const idealPositionRef = useRef(null);
    const calculatePosition = useCallback(() => {
        if (!triggerRef.current)
            return;
        const rect = triggerRef.current.getBoundingClientRect();
        const computedStyles = {
            position: 'absolute',
        };
        const offset = 10;
        let top = 0;
        let left = 0;
        switch (position) {
            case 'top':
                top = rect.top + window.scrollY - offset;
                left = rect.left + window.scrollX + rect.width / 2;
                computedStyles.transform = 'translate(-50%, -100%)';
                break;
            case 'bottom':
                top = rect.bottom + window.scrollY + offset;
                left = rect.left + window.scrollX + rect.width / 2;
                computedStyles.transform = 'translateX(-50%)';
                break;
            case 'left':
                top = rect.top + window.scrollY + rect.height / 2;
                left = rect.left + window.scrollX - offset;
                computedStyles.transform = 'translate(-100%, -50%)';
                break;
            case 'right':
                top = rect.top + window.scrollY + rect.height / 2;
                left = rect.right + window.scrollX + offset;
                computedStyles.transform = 'translateY(-50%)';
                break;
            default:
                break;
        }
        idealPositionRef.current = { top, left };
        computedStyles.top = top;
        computedStyles.left = left;
        setTooltipStyles(computedStyles);
        setArrowOffset(0);
    }, [position]);
    // Nudges the tooltip back onscreen if it overflows past either edge of the
    // viewport horizontally, and shifts the arrow the opposite amount so it
    // still points at the trigger's center instead of the (now-shifted) box's.
    //
    // Deliberately computed from idealPositionRef + the tooltip's own (shift-
    // independent) width, not from the currently-rendered rect's left/right —
    // this fires more than once (rAF here, plus ResizeObserver's mandatory
    // initial callback), and if a later call measured a rect that an earlier
    // call had already shifted onscreen, it would see "no overflow" and reset
    // the arrow back to dead-center while the box stayed shifted. Basing it on
    // the stable ideal position makes every call converge on the same answer.
    const clampToViewport = useCallback(() => {
        if (!tooltipRef.current || !idealPositionRef.current)
            return;
        const width = tooltipRef.current.offsetWidth;
        const { left: idealLeft } = idealPositionRef.current;
        const unclampedLeftEdge = idealLeft - width / 2;
        const unclampedRightEdge = idealLeft + width / 2;
        let shiftX = 0;
        if (unclampedLeftEdge < VIEWPORT_MARGIN) {
            shiftX = VIEWPORT_MARGIN - unclampedLeftEdge;
        }
        else if (unclampedRightEdge > window.innerWidth - VIEWPORT_MARGIN) {
            shiftX = window.innerWidth - VIEWPORT_MARGIN - unclampedRightEdge;
        }
        const maxArrowOffset = Math.max(width / 2 - ARROW_EDGE_PADDING, 0);
        const clampedArrowOffset = Math.max(-maxArrowOffset, Math.min(maxArrowOffset, -shiftX));
        setTooltipStyles((prev) => (Object.assign(Object.assign({}, prev), { left: idealLeft + shiftX })));
        setArrowOffset(clampedArrowOffset);
    }, []);
    const handleMouseEnter = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        calculatePosition();
        timeoutRef.current = window.setTimeout(() => {
            setVisible(true);
        }, delay);
    };
    const handleMouseLeave = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        hideTimeoutRef.current = window.setTimeout(() => {
            setVisible(false);
        }, 100);
    };
    const handleTooltipMouseEnter = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };
    const handleTooltipMouseLeave = () => {
        setVisible(false);
    };
    useEffect(() => {
        const handleResize = () => {
            calculatePosition();
            // Deferred a frame so the DOM has the fresh (unclamped) position
            // before we measure it — clampToViewport would otherwise read stale
            // coordinates from before this resize.
            requestAnimationFrame(() => clampToViewport());
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [calculatePosition, clampToViewport]);
    // Once the tooltip is actually in the DOM — and whenever its own size
    // changes, e.g. content reflow — check whether it needs nudging onscreen.
    // Deliberately not keyed on tooltipStyles: clampToViewport's own writes to
    // it would otherwise re-trigger this effect and immediately "correct" the
    // just-applied shift back to zero.
    useEffect(() => {
        if (!visible)
            return undefined;
        const frame = requestAnimationFrame(() => clampToViewport());
        const el = tooltipRef.current;
        const observer = el ? new ResizeObserver(() => clampToViewport()) : null;
        if (el && observer)
            observer.observe(el);
        return () => {
            cancelAnimationFrame(frame);
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
        };
    }, [visible, clampToViewport]);
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: triggerRef, className: styles.trigger, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave }, children),
        visible && (React.createElement(Portal, null,
            React.createElement("div", { ref: tooltipRef, className: classNames(styles.tooltip, {
                    [styles[theme]]: theme !== 'none',
                    [styles[position]]: theme !== 'none',
                }), style: Object.assign(Object.assign({}, tooltipStyles), { 'maxWidth': maxWidth === false ? undefined : maxWidth, '--arrow-offset': `${arrowOffset}px` }), onMouseEnter: handleTooltipMouseEnter, onMouseLeave: handleTooltipMouseLeave },
                theme === 'none' && content,
                theme === 'inverse' && (React.createElement("div", { className: classNames(styles.content, styles.inverse, styles[size]) }, content)),
                theme === 'default' && (React.createElement("div", { className: classNames(styles.content, styles.default, styles[size]) }, content)))))));
}
export default Tooltip;
